import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Card, Tag, Input, Button, message, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { userManagementService } from "../../api/enhancedApi";
import { formatDate } from "../../utils/dateUtils";
import useQuickSearch from "../../hooks/useQuickSearch";
import CollapsibleFilterBar from "../../components/common/CollapsibleFilterBar";
import PermissionListFilter from "./components/PermissionListFilter";
import { describePermissionUsage } from "./permissionPageUsage";

function permissionModule(name) {
    const index = String(name || "").indexOf(":");
    return index === -1 ? String(name || "") : String(name).slice(0, index);
}

function permissionAction(name) {
    const index = String(name || "").lastIndexOf(":");
    return index === -1 ? "" : String(name).slice(index + 1);
}

const PermissionList = () => {
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });

    const fetchPermissions = useCallback(async () => {
        setLoading(true);
        try {
            const data = await userManagementService.fetchPermissions();
            setPermissions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to fetch permissions:", error);
            message.error("获取权限列表失败");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    const moduleOptions = useMemo(() => {
        const modules = [...new Set(permissions.map((item) => permissionModule(item.name)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
        return modules.map((value) => ({ value, label: value }));
    }, [permissions]);

    const actionOptions = useMemo(() => {
        const actions = [...new Set(permissions.map((item) => permissionAction(item.name)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
        return actions.map((value) => ({ value, label: value }));
    }, [permissions]);

    const filteredPermissions = useMemo(() => {
        const keyword = (filterParams.search || "").trim().toLowerCase();
        return permissions.filter((item) => {
            if (keyword) {
                const usage = describePermissionUsage(item.name, item.description);
                const haystack = `${item.name || ""} ${item.description || ""} ${usage.searchText || ""}`.toLowerCase();
                if (!haystack.includes(keyword)) return false;
            }
            if (filterParams.module && permissionModule(item.name) !== filterParams.module) return false;
            if (filterParams.action && permissionAction(item.name) !== filterParams.action) return false;
            return true;
        });
    }, [permissions, filterParams]);

    const handleFilterSearch = useCallback((values) => {
        setFilterParams((prev) => {
            const next = {};
            if (prev.search) next.search = prev.search;
            if (values.module) next.module = values.module;
            if (values.action) next.action = values.action;
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const quickSearch = useQuickSearch({ setFilterParams, setPagination });

    const handleFilterReset = useCallback(() => {
        quickSearch.clear();
        setFilterParams({});
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [quickSearch]);

    const filterSummaryItems = useMemo(() => {
        const items = [];
        if (filterParams.module) items.push({ label: "业务模块", value: filterParams.module });
        if (filterParams.action) items.push({ label: "操作", value: filterParams.action });
        return items;
    }, [filterParams]);

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
            sorter: (a, b) => a.id - b.id,
        },
        {
            title: "权限代码",
            dataIndex: "name",
            key: "name",
            width: 250,
            render: (text) => <Tag color="blue">{text}</Tag>,
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "对应页面",
            key: "page_usage",
            width: 360,
            render: (_, record) => {
                const usage = describePermissionUsage(record.name, record.description);
                if (usage.relation === "none") {
                    return <span style={{ color: "#8c8c8c" }}>未挂到菜单页面</span>;
                }
                const detail = (
                    <div>
                        {usage.pages.map((page) => (
                            <div key={`${page.sectionLabel}-${page.label}`}>{[page.sectionLabel, page.label].filter(Boolean).join(" / ")}</div>
                        ))}
                    </div>
                );
                return (
                    <Tooltip title={detail}>
                        <span>{usage.summary}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: "描述",
            dataIndex: "description",
            key: "description",
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 180,
            render: (date) => formatDate(date),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">权限管理</div>
                <div className="page-action-compact">
                    <Button icon={<ReloadOutlined />} onClick={fetchPermissions} loading={loading}>
                        刷新
                    </Button>
                </div>
            </div>

            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleFilterReset}
                drawerTitle="权限管理 - 筛选条件"
                quickControls={<Input placeholder="搜索权限代码 / 描述 / 页面" style={{ width: 280, height: 26 }} {...quickSearch.inputProps} />}
            >
                <PermissionListFilter onSearch={handleFilterSearch} moduleOptions={moduleOptions} actionOptions={actionOptions} autoApply />
            </CollapsibleFilterBar>

            <Card className="data-card">
                <Table
                    columns={columns}
                    dataSource={filteredPermissions}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    onChange={(nextPagination) => setPagination({ current: nextPagination.current, pageSize: nextPagination.pageSize })}
                    scroll={{ x: 1100 }}
                    size="small"
                />
            </Card>
        </div>
    );
};

export default PermissionList;
